import { Injectable } from '@angular/core';
import { Subscription } from 'rxjs';
import { OpenPanel } from '@openpanel/web';
import { AnalyticsService } from './analytics.service';
import { getServerConfig } from '@app/origami-api/server-config';
import { UserServiceModel } from '@app/origami-api/services/user.service';
import { normalizeOpenPanelApiUrl } from './open-panel-url';

@Injectable()
export class OpenPanelService {
  private client: OpenPanel | null = null;
  private analyticsSub: Subscription | null = null;
  private lastProfileId: string | null = null;

  constructor(private analytics: AnalyticsService) {}

  attachBridge(): void {
    if (this.analyticsSub) {
      return;
    }
    this.analyticsSub = new Subscription();
    this.analyticsSub.add(this.analytics.pageTrack.subscribe((x: any) => this.pageTrack(x.path)));
    this.analyticsSub.add(this.analytics.eventTrack.subscribe((x: any) => this.eventTrack(x.action, x.properties)));
    this.analyticsSub.add(this.analytics.setUsername.subscribe((x: string) => this.setUsername(x)));
    this.analyticsSub.add(this.analytics.setUserProperties.subscribe((x: any) => this.setUserProperties(x)));
    this.analyticsSub.add(this.analytics.setUserPropertiesOnce.subscribe((x: any) => this.setUserPropertiesOnce(x)));
    this.analyticsSub.add(this.analytics.setSuperProperties.subscribe((x: any) => this.setSuperProperties(x)));
    this.analyticsSub.add(this.analytics.setSuperPropertiesOnce.subscribe((x: any) => this.setSuperPropertiesOnce(x)));
  }

  /** Call after domain user is loaded; resets and re-identifies the client. */
  syncUser(userDetails: UserServiceModel.User, roleLabel: string): void {
    this.attachBridge();
    const sc = getServerConfig().serverCfg;
    const rawUrl = (sc.openPanelUrl || '').trim();
    const clientId = (sc.openPanelClientId || '').trim();
    if (!rawUrl || !clientId) {
      this.disposeClient();
      return;
    }
    const apiUrl = normalizeOpenPanelApiUrl(rawUrl);
    if (!apiUrl) {
      this.disposeClient();
      return;
    }
    this.disposeClient();
    this.lastProfileId = String(userDetails.EmailAddress);
    this.client = new OpenPanel({
      apiUrl,
      clientId,
      trackScreenViews: false,
      trackOutgoingLinks: false,
      trackAttributes: false,
      trackHashChanges: true,
    });
    try {
      void this.client.identify({
        profileId: this.lastProfileId,
        email: userDetails.EmailAddress,
        properties: {
          $name: userDetails.FullName,
        },
      });
      this.client.setGlobalProperties({
        User: userDetails.EmailAddress,
        Instance: window.location.hostname,
        UI: 'Modern',
        Role: roleLabel,
      });
    } catch {
      /* ignore identify failures */
    }
  }

  userLogout(): void {
    this.disposeClient();
  }

  private disposeClient(): void {
    try {
      this.client?.clear();
    } catch {
      /* ignore */
    }
    this.client = null;
    this.lastProfileId = null;
  }

  private pageTrack(path: string): void {
    if (!this.client) {
      return;
    }
    try {
      void this.client.track('Page: ', {
        page: path,
        location: typeof window !== 'undefined' ? window.location.href : undefined,
      });
    } catch (e) {
      this.processError(e);
    }
  }

  private eventTrack(action: string, properties: unknown): void {
    if (!this.client) {
      return;
    }
    try {
      void this.client.track(action, (properties as Record<string, unknown>) || {});
    } catch (e) {
      this.processError(e);
    }
  }

  private setUsername(userName: string): void {
    if (!this.client) {
      return;
    }
    this.lastProfileId = String(userName);
    try {
      void this.client.identify({ profileId: this.lastProfileId });
    } catch (e) {
      this.processError(e);
    }
  }

  private setUserProperties(properties: Record<string, unknown>): void {
    if (!this.client || !this.lastProfileId) {
      return;
    }
    try {
      void this.client.identify({
        profileId: this.lastProfileId,
        properties,
      });
    } catch (e) {
      this.processError(e);
    }
  }

  private setUserPropertiesOnce(properties: Record<string, unknown>): void {
    this.setUserProperties(properties);
  }

  private setSuperProperties(properties: Record<string, unknown>): void {
    if (!this.client) {
      return;
    }
    try {
      this.client.setGlobalProperties(properties);
    } catch (e) {
      this.processError(e);
    }
  }

  private setSuperPropertiesOnce(properties: Record<string, unknown>): void {
    this.setSuperProperties(properties);
  }

  private processError(e: unknown): void {
    if (!(e instanceof ReferenceError)) {
      throw e;
    }
  }
}
